import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API, useCart } from '@/App';
import { ArrowLeft, Plus, Minus, ShoppingCart, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const ProductDetail = () => {
  const { slug, productId } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState({});
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchProduct();
  }, [slug, productId]);

  const fetchProduct = async () => {
    try {
      const res = await axios.get(`${API}/public/stores/${slug}/products/${productId}`);
      setProduct(res.data);
      if (res.data.variants?.length > 0) {
        const defaultVariant = res.data.variants.find(v => v.is_default) || res.data.variants[0];
        setSelectedVariant(defaultVariant.id);
      }
    } catch (e) {
      toast.error('Produto não encontrado');
      navigate(`/${slug}`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddonChange = (addonId, quantity) => {
    setSelectedAddons(prev => ({
      ...prev,
      [addonId]: Math.max(0, Math.min(quantity, product.addons.find(a => a.id === addonId)?.max_quantity || 10))
    }));
  };

  const getPrice = () => {
    if (!product) return 0;
    let price = product.promo_price || product.price;
    
    if (selectedVariant) {
      const variant = product.variants.find(v => v.id === selectedVariant);
      if (variant) price += variant.price_modifier;
    }
    
    return price;
  };

  const getAddonsTotal = () => {
    if (!product) return 0;
    return Object.entries(selectedAddons).reduce((sum, [addonId, qty]) => {
      const addon = product.addons.find(a => a.id === addonId);
      return sum + (addon ? addon.price * qty : 0);
    }, 0);
  };

  const getTotal = () => {
    return (getPrice() + getAddonsTotal()) * quantity;
  };

  const handleAddToCart = () => {
    if (!product) return;
    
    const variant = selectedVariant ? product.variants.find(v => v.id === selectedVariant) : null;
    const addons = Object.entries(selectedAddons)
      .filter(([_, qty]) => qty > 0)
      .map(([addonId, qty]) => {
        const addon = product.addons.find(a => a.id === addonId);
        return { id: addonId, name: addon?.name, price: addon?.price, quantity: qty };
      });

    addItem({
      product_id: product.id,
      product_name: product.name,
      quantity,
      unit_price: getPrice(),
      variant_id: variant?.id || null,
      variant_name: variant?.name || null,
      addons,
      notes
    }, slug);

    navigate(`/${slug}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1a2e] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#ff6b00]"></div>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen bg-[#1a1a2e] pb-32">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#0a0a0f] border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <Button
              size="icon"
              variant="ghost"
              onClick={() => navigate(`/${slug}`)}
              className="text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-bold text-white truncate">{product.name}</h1>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto">
        {/* Product Image */}
        {product.images?.length > 0 ? (
          <div className="aspect-video bg-[#0f0f15]">
            <img
              src={product.images[0]}
              alt={product.name}
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <div className="aspect-video bg-[#0f0f15] flex items-center justify-center">
            <span className="text-6xl">🍺</span>
          </div>
        )}

        {/* Product Info */}
        <div className="p-4 space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">{product.name}</h2>
            {product.description && (
              <p className="text-white/60">{product.description}</p>
            )}
            <div className="flex items-center gap-3 mt-3">
              {product.promo_price ? (
                <>
                  <span className="text-white/40 line-through text-lg">R$ {product.price.toFixed(2)}</span>
                  <span className="text-[#ff6b00] font-bold text-2xl">R$ {product.promo_price.toFixed(2)}</span>
                </>
              ) : (
                <span className="text-[#ff6b00] font-bold text-2xl">R$ {product.price.toFixed(2)}</span>
              )}
            </div>
          </div>

          {/* Variants */}
          {product.variants?.length > 0 && (
            <div className="card-adegga p-4">
              <h3 className="font-bold text-white mb-3">Escolha uma opção</h3>
              <RadioGroup value={selectedVariant} onValueChange={setSelectedVariant}>
                {product.variants.map(variant => (
                  <div key={variant.id} className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value={variant.id} id={variant.id} />
                      <Label htmlFor={variant.id} className="text-white cursor-pointer">
                        {variant.name}
                      </Label>
                    </div>
                    {variant.price_modifier !== 0 && (
                      <span className="text-white/60">
                        {variant.price_modifier > 0 ? '+' : ''} R$ {variant.price_modifier.toFixed(2)}
                      </span>
                    )}
                  </div>
                ))}
              </RadioGroup>
            </div>
          )}

          {/* Addons */}
          {product.addons?.length > 0 && (
            <div className="card-adegga p-4">
              <h3 className="font-bold text-white mb-3">Adicionais</h3>
              <div className="space-y-3">
                {product.addons.map(addon => (
                  <div key={addon.id} className="flex items-center justify-between">
                    <div>
                      <span className="text-white">{addon.name}</span>
                      <span className="text-[#ff6b00] ml-2">+ R$ {addon.price.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="icon"
                        variant="secondary"
                        className="h-8 w-8 bg-white/10"
                        onClick={() => handleAddonChange(addon.id, (selectedAddons[addon.id] || 0) - 1)}
                      >
                        <Minus className="w-4 h-4" />
                      </Button>
                      <span className="w-6 text-center text-white">{selectedAddons[addon.id] || 0}</span>
                      <Button
                        size="icon"
                        variant="secondary"
                        className="h-8 w-8 bg-white/10"
                        onClick={() => handleAddonChange(addon.id, (selectedAddons[addon.id] || 0) + 1)}
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="card-adegga p-4">
            <h3 className="font-bold text-white mb-3">Observações</h3>
            <Textarea
              placeholder="Ex: Bem gelada, sem gelo..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="bg-[#0f0f15] border-white/10 text-white min-h-[80px]"
            />
          </div>
        </div>
      </div>

      {/* Add to Cart Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-[#0a0a0f] border-t border-white/5 p-4 z-40">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <div className="flex items-center gap-3">
            <Button
              size="icon"
              variant="secondary"
              className="bg-white/10"
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
            >
              <Minus className="w-4 h-4" />
            </Button>
            <span className="text-xl font-bold text-white w-8 text-center">{quantity}</span>
            <Button
              size="icon"
              variant="secondary"
              className="bg-white/10"
              onClick={() => setQuantity(quantity + 1)}
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <Button
            className="flex-1 btn-primary h-12 text-lg"
            onClick={handleAddToCart}
            data-testid="add-to-cart-btn"
          >
            Adicionar R$ {getTotal().toFixed(2)}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
